/**
 * Auth-screen shell — wraps the centered card layout used by login,
 * registration, and password-recovery screens. Keeps the brand mark and
 * spacing identical across all four so users feel they're inside the same
 * flow rather than three separate apps.
 */

import type { ReactNode } from "react";

import { t } from "../i18n";
import { Footer } from "./Footer";
import { Card } from "./ui";

export interface AuthShellProps {
  /** Big headline rendered under the brand mark. Defaults to app name. */
  title?: string;
  /** Subtitle line under the headline. Defaults to app tagline. */
  description?: string;
  children: ReactNode;
}

export function AuthShell({ title, description, children }: AuthShellProps) {
  // Layout: column with a flex-1 spacer that vertically centres the card
  // (matches the previous look) and Footer pinned at the bottom. The
  // footer needs to be reachable here so unauthenticated users can read
  // the privacy notice and ToS before signing up — see
  // docs/data-visibility-and-compliance.md § 9.
  return (
    <div className="flex min-h-screen flex-col bg-ink-50">
      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <Card padding="lg">
            <div className="mb-6 text-center">
              <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary font-semibold text-primary-fg">
                {t.app.brandInitial}
              </div>
              <h1 className="font-semibold text-ink-900 text-xl">{title ?? t.app.name}</h1>
              <p className="mt-1 text-ink-500 text-sm">{description ?? t.app.tagline}</p>
            </div>
            {children}
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
